import { useGetSingleBlog, useUpdateBlog } from '@app/Hooks/use-blogs';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildToolbar } from './new-story-helper';
import {
  CharBar,
  renderMarkdown,
  SidebarSection,
  validationSchema
} from './new-story-helper';
import {
  Eye,
  Edit3,
  ChevronDown,
  ChevronUp,
  Save,
  Send,
  Calendar,
  FileText,
  Globe,
  AlignLeft,
  Image,
  Loader2
} from 'lucide-react';
import { FileUploadToCloud } from '@app/services/fileUploadService';

const EditStory = () => {
  const mode = 'edit';
  const { id } = useParams();
  const { data, isLoading } = useGetSingleBlog(id);
  const { mutateAsync, isPending } = useUpdateBlog();
  const [editorMode, setEditorMode] = useState('write');
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [image, setImage] = useState(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      title: data?.title || '',
      content: data?.content || '',
      excerpt: data?.excerpt || '',
      featured_image: data?.featured_image || '',
      author: data?.author || '',
      categories: data?.categories || [],
      tags: data?.tags || [],
      status: data?.status || 'draft',
      publish_date: data?.publish_date || '',
      seo_title: data?.seo_title || '',
      seo_description: data?.seo_description || ''
    },
    onSubmit: async (values, helpers) => {
      const cleaned = Object.fromEntries(
        Object.entries(values).filter(([k, v]) => {
          if (Array.isArray(v)) return v.length > 0; // exclude empty arrays
          return v !== '' && v !== null && v !== undefined; // exclude empty strings/nulls
        })
      );

      const uploadedImage =
        typeof image === 'string'
          ? image
          : image === null
          ? null
          : await FileUploadToCloud(image);
      const data = {
        ...cleaned,
        ...(uploadedImage && {
          featured_image:
            typeof uploadedImage === 'string'
              ? uploadedImage // existing URL
              : uploadedImage?.secure_url // newly uploaded URL
        })
      };

      await mutateAsync({ id, blog: data });
      helpers.resetForm();
      imageRef.current.value = '';
    },
    enableReinitialize: true,
    validationSchema
  });

  useEffect(() => {
    if (!data) return;
    setImage(data?.featured_image);
  }, [data]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        document.activeElement === contentRef.current
      ) {
        if (e.key === 'b') {
          e.preventDefault();
          insertMarkdown('**', '**', 'bold text');
        }
        if (e.key === 'i') {
          e.preventDefault();
          insertMarkdown('*', '*', 'italic text');
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Editor helpers ──────────────────────────────────────────────────────────

  const insertMarkdown = useCallback(
    (prefix, suffix = '', placeholder = 'text') => {
      const el = contentRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = el.value.substring(start, end) || placeholder;
      const newVal =
        el.value.substring(0, start) +
        prefix +
        selected +
        suffix +
        el.value.substring(end);
      formik.setFieldValue('content', newVal);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(
          start + prefix.length,
          start + prefix.length + selected.length
        );
      }, 0);
    },
    [formik]
  );

  const insertLinePrefix = useCallback(
    (prefix) => {
      const el = contentRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const lineStart = el.value.lastIndexOf('\n', start - 1) + 1;
      // Toggle: remove prefix if already present
      const lineHasPrefix = el.value.substring(lineStart).startsWith(prefix);
      const newVal = lineHasPrefix
        ? el.value.substring(0, lineStart) +
          el.value.substring(lineStart + prefix.length)
        : el.value.substring(0, lineStart) + prefix + el.value.substring(lineStart);
      formik.setFieldValue('content', newVal);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(
          start + (lineHasPrefix ? -prefix.length : prefix.length),
          start + (lineHasPrefix ? -prefix.length : prefix.length)
        );
      }, 0);
    },
    [formik]
  );

  const insertBlock = useCallback(
    (text) => {
      const el = contentRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const newVal = el.value.substring(0, start) + text + el.value.substring(start);
      formik.setFieldValue('content', newVal);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    },
    [formik]
  );

  if (isLoading) {
    return (
      <div className="grid place-items-center h-screen">
        <Loader2 className="animate-spin" color="#003232" />
      </div>
    );
  }

  const toolbar = buildToolbar(insertMarkdown, insertLinePrefix, insertBlock);

  // ── Status config ───────────────────────────────────────────────────────────

  const statusConfig = {
    draft: {
      label: 'Draft',
      active: 'text-zinc-300 bg-zinc-700/60 border-zinc-600/40',
      inactive: 'text-black/25 border-transparent hover:text-black/40'
    },
    published: {
      label: 'Published',
      active: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
      inactive: 'text-black/25 border-transparent hover:text-black/40'
    },
    archived: {
      label: 'Archived',
      active: 'text-rose-400 bg-rose-500/10 border-rose-500/25',
      inactive: 'text-black/25 border-transparent hover:text-black/40'
    }
  };

  // ── Field helpers ───────────────────────────────────────────────────────────

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur
  });

  const sidebarInput =
    'w-full bg-white/[0.04] border border-black/10 rounded-lg px-3 py-2 text-black/70 text-xs placeholder-black/20 outline-none focus:border-[#003232]/40 transition-colors';

  const errorFor = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <p className="mt-1 text-red-400 text-[10px]">{formik.errors[name]}</p>
    ) : null;

  const wordCount = formik.values.content.trim().split(/\s+/).filter(Boolean).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div>
      <input
        ref={imageRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setImage(file);
        }}
      />
      <form
        className="flex overflow-hidden flex-col h-screen"
        onSubmit={formik.handleSubmit}
      >
        {/* Top Bar */}
        <header className="shrink-0 flex items-center justify-between px-5 py-2.5 border-b border-white/[0.06]">
          <div className="flex gap-3 items-center min-w-0">
            <div className="flex justify-center items-center w-5 h-5 bg-amber-500 rounded shrink-0">
              <FileText size={11} className="text-black" />
            </div>
            <span className="text-xs text-black truncate">
              {mode === 'edit' ? 'Editing post' : 'New post'}
            </span>
            {formik.values.content && (
              <span className="hidden text-xs text-black sm:inline">
                {wordCount.toLocaleString()} words · {readTime} min read
              </span>
            )}
          </div>

          <div className="flex gap-2 items-center shrink-0">
            {/* Status switcher */}
            <div className="hidden gap-0.5 p-0.5 rounded-lg sm:flex">
              {Object.entries(statusConfig).map(([val, cfg]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => formik.setFieldValue('status', val)}
                  className={`px-3 py-1 text-[11px] rounded-md border transition-all duration-300 ease-in-out ${
                    formik.values.status === val ? cfg.active : cfg.inactive
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                formik.setFieldValue('status', 'draft');
                setTimeout(() => formik.submitForm(), 0);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-black/50 hover:text-black bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-lg transition-all"
            >
              <Save size={13} />
              <span className="hidden sm:inline">Save draft</span>
            </button>

            <button
              type="submit"
              disabled={isPending}
              className="flex gap-1.5 items-center px-4 py-1.5 text-xs font-semibold text-white bg-[#003232] rounded-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed duration-300 ease-in-out"
            >
              <Send size={13} />
              {isPending
                ? 'Saving…'
                : mode === 'edit'
                ? 'Update'
                : formik.values.status === 'published'
                ? 'Publish'
                : 'Save'}
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex overflow-hidden flex-col flex-1 h-full lg:flex-row">
          <main className="flex-1 flex flex-col overflow-hidden bg-[#FAFAF7]">
            {/* Title */}
            <div className="px-12 pt-10 pb-3 xl:px-20 shrink-0">
              <input
                {...fieldProps('title')}
                placeholder="Your story title…"
                maxLength={201}
                className="writing-font title-input w-full bg-transparent text-[#1A1A1A] text-4xl font-light leading-snug border-none outline-none"
              />
              {errorFor('title')}
              <div className="flex gap-3 items-center mt-3">
                <div className="flex-1 h-px bg-gradient-to-r from-[#DDD] to-transparent" />
                <span className="text-[#B8B8A8] text-xs">
                  {(formik.values.title || '').length}/200
                </span>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-12 pb-2 xl:px-20 shrink-0">
              <div className="flex items-center gap-0.5 bg-[#EFEFE8] rounded-xl px-1.5 py-1 w-fit flex-wrap">
                {toolbar.map((btn, i) =>
                  btn.type === 'sep' ? (
                    <div key={i} className="w-px h-4 bg-[#D4D4C8] mx-0.5" />
                  ) : (
                    <button
                      key={i}
                      type="button"
                      onClick={btn.action}
                      title={btn.label}
                      className="flex items-center justify-center w-7 h-7 rounded-lg text-[#777770] hover:text-[#1A1A1A] hover:bg-white/80 transition-all"
                    >
                      {btn.icon ? (
                        <btn.icon size={13} />
                      ) : (
                        <span className="text-[11px] font-semibold">
                          {btn.label}
                        </span>
                      )}
                    </button>
                  )
                )}

                <div className="w-px h-4 bg-[#D4D4C8] mx-0.5" />

                <button
                  type="button"
                  onClick={() => setEditorMode('write')}
                  title="Write mode"
                  className={`flex items-center gap-1 px-2 h-7 rounded-lg text-[11px] transition-all ${
                    editorMode === 'write'
                      ? 'bg-white text-[#1A1A1A] shadow-sm font-medium'
                      : 'text-[#999990] hover:text-[#1A1A1A]'
                  }`}
                >
                  <Edit3 size={11} />
                  Write
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode('preview')}
                  title="Preview mode"
                  className={`flex items-center gap-1 px-2 h-7 rounded-lg text-[11px] transition-all ${
                    editorMode === 'preview'
                      ? 'bg-white text-[#1A1A1A] shadow-sm font-medium'
                      : 'text-[#999990] hover:text-[#1A1A1A]'
                  }`}
                >
                  <Eye size={11} />
                  Preview
                </button>
              </div>
            </div>

            {/* Editor */}
            <div className="overflow-y-auto flex-1 px-12 pb-20 xl:px-20">
              {editorMode === 'write' ? (
                <>
                  <textarea
                    ref={contentRef}
                    {...fieldProps('content')}
                    placeholder="Tell your story… (Markdown supported)"
                    className="writing-font content-textarea w-full bg-transparent text-[#2A2A2A] text-lg leading-relaxed border-none outline-none resize-none min-h-[400px]"
                    rows={20}
                    spellCheck
                  />
                  {errorFor('content')}
                </>
              ) : (
                <div
                  className="py-2 max-w-none text-lg leading-relaxed prose-preview writing-font"
                  dangerouslySetInnerHTML={{
                    __html: formik.values.content
                      ? renderMarkdown(formik.values.content)
                      : '<p className="text-[#B8B8A8] italic">Nothing to preview yet…</p>'
                  }}
                />
              )}
            </div>
          </main>

          <aside className="w-full xl:w-72 shrink-0 flex flex-col bg-white border-l border-white/[0.05] overflow-y-auto thin-scroll">
            <div className="p-4 space-y-5">
              {/* Excerpt */}
              <SidebarSection label="Excerpt" icon={AlignLeft}>
                <textarea
                  {...fieldProps('excerpt')}
                  placeholder="Short summary for previews…"
                  rows={3}
                  maxLength={501}
                  className={`resize-none ${sidebarInput}`}
                />
                <CharBar current={(formik.values.excerpt || '').length} max={500} />
                {errorFor('excerpt')}
              </SidebarSection>

              {/* Divider */}
              <div className="h-px bg-black/[0.04]" />

              {/* Featured image */}
              <SidebarSection label="Featured Image" icon={Image}>
                <div className="flex gap-2 items-center px-3 py-2 rounded-lg border border-black/10">
                  <div className="flex-1 text-sm line-clamp-1">
                    {typeof image === 'string'
                      ? image
                      : typeof image === 'object'
                      ? image?.name
                      : `Select an image`}
                  </div>
                  <button
                    type="button"
                    onClick={() => imageRef?.current?.click()}
                    className="text-[10px] bg-[#003232] text-white px-2 py-1 rounded-lg"
                  >
                    Choose Image
                  </button>
                </div>

                {image && (
                  <div className="overflow-hidden mt-2 rounded-lg border border-white/10">
                    <img
                      src={
                        typeof image === 'string'
                          ? image
                          : URL.createObjectURL(image)
                      }
                      alt="Featured"
                      className="object-cover w-full h-24"
                    />
                  </div>
                )}
              </SidebarSection>

              <div className="h-px bg-black/[0.04]" />

              {/* Author */}
              {/* <SidebarSection label="Author" icon={User}>
                <input
                  {...fieldProps('author')}
                  placeholder="User ID…"
                  className={sidebarInput}
                />
              </SidebarSection> */}

              <div className="h-px bg-black/[0.04]" />

              {/* Publish date */}
              <SidebarSection label="Publish Date" icon={Calendar}>
                <input
                  type="datetime-local"
                  {...fieldProps('publish_date')}
                  className={`${sidebarInput} [color-scheme:dark]`}
                />
              </SidebarSection>

              <div className="h-px bg-black/[0.04]" />

              {/* SEO */}
              <div className="border border-black/[0.07] rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSeoExpanded((p) => !p)}
                  className="flex justify-between items-center px-3.5 py-2.5 w-full transition-colors text-black/30 hover:text-black/50"
                >
                  <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest">
                    <Globe size={11} />
                    SEO Settings
                  </span>
                  {seoExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
                {seoExpanded && (
                  <div className="border-t border-white/[0.05] px-3.5 pb-4 pt-3 space-y-3">
                    {/* SEO Title */}
                    <div>
                      <label className="text-black/25 text-[10px] mb-1 block">
                        SEO Title
                      </label>
                      <input
                        {...fieldProps('seo_title')}
                        placeholder="Search engine title…"
                        maxLength={61}
                        className={sidebarInput}
                      />
                      <CharBar
                        current={(formik.values.seo_title || '').length}
                        max={60}
                      />
                      {errorFor('seo_title')}
                    </div>

                    {/* SEO Description */}
                    <div>
                      <label className="text-black/25 text-[10px] mb-1 block">
                        SEO Description
                      </label>
                      <textarea
                        {...fieldProps('seo_description')}
                        placeholder="Meta description…"
                        rows={3}
                        maxLength={161}
                        className={`resize-none ${sidebarInput}`}
                      />
                      <CharBar
                        current={(formik.values.seo_description || '').length}
                        max={160}
                      />
                      {errorFor('seo_description')}
                    </div>

                    {/* SERP Preview */}
                    {(formik.values.seo_title || formik.values.seo_description) && (
                      <div className="mt-2 p-2.5 bg-white/[0.03] rounded-lg border border-black/[0.06]">
                        <p className="text-black/20 text-[9px] uppercase tracking-widest mb-2">
                          SERP Preview
                        </p>
                        <p className="text-xs leading-snug text-blue-400 truncate">
                          {formik.values.seo_title ||
                            formik.values.title ||
                            'Untitled'}
                        </p>
                        <p className="text-emerald-500/50 text-[10px] mt-0.5">
                          yourdomain.com/blog/post-slug
                        </p>
                        {formik.values.seo_description && (
                          <p className="text-black/30 text-[10px] mt-1 leading-relaxed line-clamp-2">
                            {formik.values.seo_description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Validation summary */}
              {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
                <div className="p-3 rounded-lg border bg-red-500/10 border-red-500/20">
                  <p className="text-red-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                    Please fix:
                  </p>
                  {Object.values(formik.errors).map((err, i) => (
                    <p key={i} className="text-red-400/80 text-[10px] leading-snug">
                      · {err}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
};

export default EditStory;
